import React from 'react';
import {
  useGetUserSettingQuery,
  useCreateUserSettingMutation,
  useUpdateUserSettingMutation,
} from '@src/features/settings/redux/api';
import {
  CameraActionEnum,
  CameraControlKey,
  ControllerModeEnum,
  DEFAULT_CAMERA_CONTROLS,
  USER_SETTING_KEYS,
} from '../../config/controller-constants';

export const useControllerSettings = () => {
  const [controllerMode, setControllerMode] = React.useState<ControllerModeEnum>(ControllerModeEnum.MODE_2);
  const [cameraControls, setCameraControls] = React.useState<Record<CameraControlKey, CameraActionEnum>>(DEFAULT_CAMERA_CONTROLS);

  const {
    data: modeData,
    isSuccess: isModeLoaded,
    isError: isModeError,
  } = useGetUserSettingQuery(USER_SETTING_KEYS.CONTROLLER_MODE);

  const {
    data: cameraData,
    isSuccess: isCameraLoaded,
    isError: isCameraError,
  } = useGetUserSettingQuery(USER_SETTING_KEYS.CAMERA_CONTROLS);

  const [createUserSetting] = useCreateUserSettingMutation();
  const [updateUserSetting] = useUpdateUserSettingMutation();

  const modeExists = isModeLoaded && !!modeData?.value;
  const cameraExists = isCameraLoaded && !!cameraData?.value;

  React.useEffect(() => {
    if (isModeLoaded && modeData?.value) {
      const value = modeData.value as ControllerModeEnum;
      if (Object.values(ControllerModeEnum).includes(value)) {
        setControllerMode(value);
      }
    }
  }, [isModeLoaded, modeData]);

  React.useEffect(() => {
    if (isCameraLoaded && cameraData?.value) {
      try {
        const parsed = JSON.parse(cameraData.value) as Record<CameraControlKey, CameraActionEnum>;
        setCameraControls(parsed);
      } catch {
        // fallback to defaults
      }
    }
  }, [isCameraLoaded, cameraData]);

  const saveSetting = React.useCallback(
    (key: string, value: string, exists: boolean) => {
      return exists
        ? updateUserSetting({ key, value })
        : createUserSetting({ key, value });
    },
    [updateUserSetting, createUserSetting],
  );

  const handleModeChange = React.useCallback(
    (mode: ControllerModeEnum) => {
      setControllerMode(mode);
      saveSetting(USER_SETTING_KEYS.CONTROLLER_MODE, mode, modeExists);
    },
    [saveSetting, modeExists],
  );

  const handleCameraControlChange = React.useCallback(
    (key: CameraControlKey, value: CameraActionEnum) => {
      setCameraControls((prev) => {
        const updated = { ...prev, [key]: value };
        saveSetting(USER_SETTING_KEYS.CAMERA_CONTROLS, JSON.stringify(updated), cameraExists);
        return updated;
      });
    },
    [saveSetting, cameraExists],
  );

  const handleReset = React.useCallback(() => {
    setControllerMode(ControllerModeEnum.MODE_2);
    setCameraControls(DEFAULT_CAMERA_CONTROLS);
    saveSetting(USER_SETTING_KEYS.CONTROLLER_MODE, ControllerModeEnum.MODE_2, modeExists);
    saveSetting(USER_SETTING_KEYS.CAMERA_CONTROLS, JSON.stringify(DEFAULT_CAMERA_CONTROLS), cameraExists);
  }, [saveSetting, modeExists, cameraExists]);

  const isLoading = (!isModeLoaded && !isModeError) || (!isCameraLoaded && !isCameraError);

  return {
    controllerMode,
    cameraControls,
    isLoading,
    handleModeChange,
    handleCameraControlChange,
    handleReset,
  };
};

export default useControllerSettings;
